/**
 * Residual builders for 2D geometric + dimensional constraints.
 *
 * Every constraint exposes one or more scalar residuals r(x) that the
 * solver drives to zero. Each residual carries its analytic Jacobian
 * (∂r/∂x_i sparse row) so Gauss-Newton updates are exact — no numeric
 * differentiation needed.
 *
 * Geometric constraints implemented (AutoCAD parity):
 *   - Coincident      : two points coincide (2 residuals)
 *   - Collinear       : three points on one line (1 residual)
 *   - Parallel        : two lines parallel (cross product = 0)
 *   - Perpendicular   : two lines perpendicular (dot product = 0)
 *   - Horizontal      : line is horizontal (endY - startY = 0)
 *   - Vertical        : line is vertical (endX - startX = 0)
 *   - Tangent         : circle tangent to line (|center-proj| = radius)
 *   - Equal           : two lengths or radii equal
 *   - Symmetric       : two points symmetric across a line (2 residuals)
 *   - Concentric      : two circles share center (2 residuals)
 *   - Fix             : handled via ConstraintVariable.fixed (no residual)
 *   - Smooth          : G1 continuity between spline/arc endpoints
 *                        (modelled as two tangent-vector collinearities)
 *
 * Dimensional constraints (Aligned, Radial, Diameter) — DYN-3 adds
 * these on top of DYN-2's Horizontal / Vertical / Angular (which the
 * action pipeline already drives via direct propagation; including
 * solver residuals here lets them participate in over-constrained
 * systems rather than only fire-and-forget Move actions).
 *
 * Tests: see constraintSolver.test.ts for convergence & stability
 * scenarios covering each residual type.
 */

import type {
  ArcRef,
  CircleRef,
  ConstraintResidual,
  LineRef,
  PointRef,
} from "./constraintSolver.types";

// ═══════════════════════════════════════════════════════════════════════
// Tiny helpers — sparse-row Jacobian literals read cleaner with them.
// ═══════════════════════════════════════════════════════════════════════

type Jrow = Record<string, number>;

function addJ(out: Jrow, key: string, value: number): void {
  // Merge repeated keys (a variable that appears twice in one Jacobian
  // row should accumulate, not overwrite — happens e.g. when the same
  // variable is used for two points in a Coincident). Zero-initialise
  // via `?? 0` so fresh entries also work.
  out[key] = (out[key] ?? 0) + value;
}

// ═══════════════════════════════════════════════════════════════════════
// 1. Coincident — two points share position.
//    Two scalar residuals: r1 = aX - bX,   r2 = aY - bY
// ═══════════════════════════════════════════════════════════════════════

export function coincidentResidual(
  a: PointRef,
  b: PointRef,
): ConstraintResidual[] {
  return [
    {
      compute: (v) => v[a.x]! - v[b.x]!,
      jacobian: () => {
        const j: Jrow = {};
        addJ(j, a.x, 1);
        addJ(j, b.x, -1);
        return j;
      },
    },
    {
      compute: (v) => v[a.y]! - v[b.y]!,
      jacobian: () => {
        const j: Jrow = {};
        addJ(j, a.y, 1);
        addJ(j, b.y, -1);
        return j;
      },
    },
  ];
}

// ═══════════════════════════════════════════════════════════════════════
// 2. Collinear — three points on one line.
//    r = (b - a) × (c - a) = (bx-ax)(cy-ay) - (by-ay)(cx-ax) = 0
// ═══════════════════════════════════════════════════════════════════════

export function collinearResidual(
  a: PointRef,
  b: PointRef,
  c: PointRef,
): ConstraintResidual {
  return {
    compute: (v) => {
      const ax = v[a.x]!;
      const ay = v[a.y]!;
      const bx = v[b.x]!;
      const by = v[b.y]!;
      const cx = v[c.x]!;
      const cy = v[c.y]!;
      return (bx - ax) * (cy - ay) - (by - ay) * (cx - ax);
    },
    jacobian: (v) => {
      const ax = v[a.x]!;
      const ay = v[a.y]!;
      const bx = v[b.x]!;
      const by = v[b.y]!;
      const cx = v[c.x]!;
      const cy = v[c.y]!;
      // r = (bx-ax)(cy-ay) - (by-ay)(cx-ax)
      // ∂r/∂ax = -(cy-ay) + (by-ay) = by - cy
      // ∂r/∂ay = -(bx-ax) + (cx-ax) = -bx + cx
      // Wait — redo:
      //   ∂r/∂ax = -(cy-ay) - (-(by-ay))(1) = -(cy-ay) + (by-ay) = by - cy
      //   ∂r/∂ay = (bx-ax)(-1) - (-1)(cx-ax) = -(bx-ax) + (cx-ax) = cx - bx
      //   ∂r/∂bx = cy - ay
      //   ∂r/∂by = -(cx - ax) = ax - cx
      //   ∂r/∂cx = -(by - ay) = ay - by
      //   ∂r/∂cy = bx - ax
      const j: Jrow = {};
      addJ(j, a.x, by - cy);
      addJ(j, a.y, cx - bx);
      addJ(j, b.x, cy - ay);
      addJ(j, b.y, ax - cx);
      addJ(j, c.x, ay - by);
      addJ(j, c.y, bx - ax);
      return j;
    },
  };
}

// ═══════════════════════════════════════════════════════════════════════
// 3. Parallel — two lines parallel (cross product of their direction = 0)
//    d1 = (line1.end - line1.start), d2 = (line2.end - line2.start)
//    r = d1.x * d2.y - d1.y * d2.x = 0
// ═══════════════════════════════════════════════════════════════════════

export function parallelResidual(
  line1: LineRef,
  line2: LineRef,
): ConstraintResidual {
  return {
    compute: (v) => {
      const dx1 = v[line1.endX]! - v[line1.startX]!;
      const dy1 = v[line1.endY]! - v[line1.startY]!;
      const dx2 = v[line2.endX]! - v[line2.startX]!;
      const dy2 = v[line2.endY]! - v[line2.startY]!;
      return dx1 * dy2 - dy1 * dx2;
    },
    jacobian: (v) => {
      const dx1 = v[line1.endX]! - v[line1.startX]!;
      const dy1 = v[line1.endY]! - v[line1.startY]!;
      const dx2 = v[line2.endX]! - v[line2.startX]!;
      const dy2 = v[line2.endY]! - v[line2.startY]!;
      // r = dx1*dy2 - dy1*dx2
      // Chain: dx1 = l1ex - l1sx, so ∂dx1/∂l1ex = 1, ∂dx1/∂l1sx = -1.
      const j: Jrow = {};
      addJ(j, line1.startX, -dy2); // ∂r/∂l1sx = ∂r/∂dx1 * -1 = dy2 * -1
      addJ(j, line1.endX, dy2); // ∂r/∂l1ex = dy2
      addJ(j, line1.startY, dx2); // ∂r/∂l1sy = -dx2 * -1 = dx2
      addJ(j, line1.endY, -dx2); // ∂r/∂l1ey = -dx2
      addJ(j, line2.startX, dy1); // ∂r/∂l2sx = -dy1 * -1 = dy1
      addJ(j, line2.endX, -dy1); // ∂r/∂l2ex = -dy1
      addJ(j, line2.startY, -dx1); // ∂r/∂l2sy = dx1 * -1 = -dx1
      addJ(j, line2.endY, dx1); // ∂r/∂l2ey = dx1
      return j;
    },
  };
}

// ═══════════════════════════════════════════════════════════════════════
// 4. Perpendicular — two lines perpendicular (dot product = 0).
//    r = d1.x * d2.x + d1.y * d2.y = 0
// ═══════════════════════════════════════════════════════════════════════

export function perpendicularResidual(
  line1: LineRef,
  line2: LineRef,
): ConstraintResidual {
  return {
    compute: (v) => {
      const dx1 = v[line1.endX]! - v[line1.startX]!;
      const dy1 = v[line1.endY]! - v[line1.startY]!;
      const dx2 = v[line2.endX]! - v[line2.startX]!;
      const dy2 = v[line2.endY]! - v[line2.startY]!;
      return dx1 * dx2 + dy1 * dy2;
    },
    jacobian: (v) => {
      const dx1 = v[line1.endX]! - v[line1.startX]!;
      const dy1 = v[line1.endY]! - v[line1.startY]!;
      const dx2 = v[line2.endX]! - v[line2.startX]!;
      const dy2 = v[line2.endY]! - v[line2.startY]!;
      // r = dx1*dx2 + dy1*dy2
      const j: Jrow = {};
      addJ(j, line1.startX, -dx2);
      addJ(j, line1.endX, dx2);
      addJ(j, line1.startY, -dy2);
      addJ(j, line1.endY, dy2);
      addJ(j, line2.startX, -dx1);
      addJ(j, line2.endX, dx1);
      addJ(j, line2.startY, -dy1);
      addJ(j, line2.endY, dy1);
      return j;
    },
  };
}

// ═══════════════════════════════════════════════════════════════════════
// 5. Horizontal — line parallel to X-axis.
//    r = endY - startY = 0
// ═══════════════════════════════════════════════════════════════════════

export function horizontalResidual(line: LineRef): ConstraintResidual {
  return {
    compute: (v) => v[line.endY]! - v[line.startY]!,
    jacobian: () => {
      const j: Jrow = {};
      addJ(j, line.endY, 1);
      addJ(j, line.startY, -1);
      return j;
    },
  };
}

// ═══════════════════════════════════════════════════════════════════════
// 6. Vertical — line parallel to Y-axis.
//    r = endX - startX = 0
// ═══════════════════════════════════════════════════════════════════════

export function verticalResidual(line: LineRef): ConstraintResidual {
  return {
    compute: (v) => v[line.endX]! - v[line.startX]!,
    jacobian: () => {
      const j: Jrow = {};
      addJ(j, line.endX, 1);
      addJ(j, line.startX, -1);
      return j;
    },
  };
}

// ═══════════════════════════════════════════════════════════════════════
// 7. Tangent — circle tangent to infinite line.
//    Signed distance from center to line === radius.
//    line direction d = end - start, normal n = (-d.y, d.x).
//    dist = ((center - start) · n) / |d|
//    r = dist² - radius²          (square form — avoids sqrt in Jacobian)
// ═══════════════════════════════════════════════════════════════════════

export function tangentResidual(
  circle: CircleRef,
  line: LineRef,
): ConstraintResidual {
  return {
    compute: (v) => {
      const cx = v[circle.centerX]!;
      const cy = v[circle.centerY]!;
      const rad = v[circle.radius]!;
      const sx = v[line.startX]!;
      const sy = v[line.startY]!;
      const ex = v[line.endX]!;
      const ey = v[line.endY]!;
      const dx = ex - sx;
      const dy = ey - sy;
      const lenSq = dx * dx + dy * dy;
      if (lenSq < 1e-18) return 0;
      // Cross product (center-start) × d = signed × |d|
      const cross = (cx - sx) * dy - (cy - sy) * dx;
      return (cross * cross) / lenSq - rad * rad;
    },
    jacobian: (v) => {
      const cx = v[circle.centerX]!;
      const cy = v[circle.centerY]!;
      const rad = v[circle.radius]!;
      const sx = v[line.startX]!;
      const sy = v[line.startY]!;
      const ex = v[line.endX]!;
      const ey = v[line.endY]!;
      const dx = ex - sx;
      const dy = ey - sy;
      const lenSq = dx * dx + dy * dy;
      const j: Jrow = {};
      if (lenSq < 1e-18) {
        addJ(j, circle.radius, -2 * rad);
        return j;
      }
      const cross = (cx - sx) * dy - (cy - sy) * dx;
      //   r = cross² / lenSq - rad²
      //   dr/dcx = 2*cross*dy / lenSq
      //   dr/dcy = 2*cross*(-dx) / lenSq
      //   dr/drad = -2*rad
      //   For line endpoints need product/quotient rules:
      //     Let u = cross², w = lenSq.  r = u/w - rad².
      //     dr/dp = (du/dp * w - u * dw/dp) / w²  for any p in {sx,sy,ex,ey}
      //
      //   cross = (cx-sx)*dy - (cy-sy)*dx
      //   dcross/dsx = -dy + 0        - ((cy-sy)*(-1))   wait — dx depends on sx through (ex - sx):
      //     dx = ex - sx, so ddx/dsx = -1, ddy/dsx = 0.
      //     dcross/dsx = d/dsx [ (cx-sx)*(ey-sy) - (cy-sy)*(ex-sx) ]
      //                = -1*(ey-sy) - (cy-sy)*(-1)
      //                = -(ey-sy) + (cy-sy)
      //                = -(ey - sy - cy + sy) = -(ey - cy) = cy - ey
      //   Simpler: just reuse expansion cross = cx*dy - sx*dy - cy*dx + sy*dx.
      //     ∂cross/∂sx = -dy + 0 - 0 + 0 ... but sy enters dy = ey - sy → ddy/∂sy = -1.
      //   Let's expand carefully:
      //     cross(cx, cy, sx, sy, ex, ey) = (cx-sx)(ey-sy) - (cy-sy)(ex-sx)
      const crossOverLenSq = cross / lenSq;
      const crossSqOverLenSqSq = (cross * cross) / (lenSq * lenSq);
      // Derivatives of cross w.r.t each variable:
      // d/dcx: (ey - sy) = dy
      // d/dcy: -(ex - sx) = -dx
      // d/dsx: d/dsx [(cx-sx)(ey-sy) - (cy-sy)(ex-sx)]
      //        = -(ey-sy) + (cy-sy) = -dy + (cy - sy)
      //        (Note: cy-sy is NOT the same as the d variable; use it directly.)
      // Simpler: just do full expansion to avoid confusion.
      // cross = cx*ey - cx*sy - sx*ey + sx*sy - cy*ex + cy*sx + sy*ex - sy*sx
      //       = cx*ey - cx*sy - sx*ey + sx*sy - cy*ex + cy*sx + sy*ex - sx*sy
      // The two ±sx*sy cancel.
      // = cx*ey - cx*sy - sx*ey - cy*ex + cy*sx + sy*ex
      // ∂/∂cx = ey - sy = dy
      // ∂/∂cy = -ex + sx = -dx
      // ∂/∂sx = -ey + cy = cy - ey
      // ∂/∂sy = -cx + ex = ex - cx
      // ∂/∂ex = -cy + sy = sy - cy
      // ∂/∂ey = cx - sx
      const dCross_dCx = dy;
      const dCross_dCy = -dx;
      const dCross_dSx = cy - ey;
      const dCross_dSy = ex - cx;
      const dCross_dEx = sy - cy;
      const dCross_dEy = cx - sx;

      // Derivatives of lenSq = dx² + dy² w.r.t each:
      // ∂/∂sx = -2*dx, ∂/∂sy = -2*dy, ∂/∂ex = 2*dx, ∂/∂ey = 2*dy
      const dLenSq_dSx = -2 * dx;
      const dLenSq_dSy = -2 * dy;
      const dLenSq_dEx = 2 * dx;
      const dLenSq_dEy = 2 * dy;

      // dr/dp = 2*cross*dCross_dp / lenSq  -  (cross²/lenSq²) * dLenSq_dp
      const twoCrossOverLenSq = (2 * cross) / lenSq;

      addJ(j, circle.centerX, twoCrossOverLenSq * dCross_dCx);
      addJ(j, circle.centerY, twoCrossOverLenSq * dCross_dCy);
      addJ(j, circle.radius, -2 * rad);
      addJ(
        j,
        line.startX,
        twoCrossOverLenSq * dCross_dSx - crossSqOverLenSqSq * dLenSq_dSx,
      );
      addJ(
        j,
        line.startY,
        twoCrossOverLenSq * dCross_dSy - crossSqOverLenSqSq * dLenSq_dSy,
      );
      addJ(
        j,
        line.endX,
        twoCrossOverLenSq * dCross_dEx - crossSqOverLenSqSq * dLenSq_dEx,
      );
      addJ(
        j,
        line.endY,
        twoCrossOverLenSq * dCross_dEy - crossSqOverLenSqSq * dLenSq_dEy,
      );
      // Silence unused-var warnings from earlier-derived helpers that we kept
      // for readability of the derivation comments above.
      void crossOverLenSq;
      return j;
    },
  };
}

// ═══════════════════════════════════════════════════════════════════════
// 8. Equal — two scalar-valued quantities equal. Generic: any two
// length or radius variables. For two lines with equal length use
// equalLengthResidual; for two circles with equal radius pass radius
// variable ids via equalValueResidual.
// ═══════════════════════════════════════════════════════════════════════

export function equalValueResidual(
  varIdA: string,
  varIdB: string,
): ConstraintResidual {
  return {
    compute: (v) => v[varIdA]! - v[varIdB]!,
    jacobian: () => {
      const j: Jrow = {};
      addJ(j, varIdA, 1);
      addJ(j, varIdB, -1);
      return j;
    },
  };
}

/**
 * Equal length — lenA² = lenB² (squared form so the Jacobian is
 * analytic and well-behaved; solving on squared length still gives
 * the correct length when residual=0).
 */
export function equalLengthResidual(
  line1: LineRef,
  line2: LineRef,
): ConstraintResidual {
  return {
    compute: (v) => {
      const dx1 = v[line1.endX]! - v[line1.startX]!;
      const dy1 = v[line1.endY]! - v[line1.startY]!;
      const dx2 = v[line2.endX]! - v[line2.startX]!;
      const dy2 = v[line2.endY]! - v[line2.startY]!;
      return dx1 * dx1 + dy1 * dy1 - (dx2 * dx2 + dy2 * dy2);
    },
    jacobian: (v) => {
      const dx1 = v[line1.endX]! - v[line1.startX]!;
      const dy1 = v[line1.endY]! - v[line1.startY]!;
      const dx2 = v[line2.endX]! - v[line2.startX]!;
      const dy2 = v[line2.endY]! - v[line2.startY]!;
      const j: Jrow = {};
      addJ(j, line1.startX, -2 * dx1);
      addJ(j, line1.endX, 2 * dx1);
      addJ(j, line1.startY, -2 * dy1);
      addJ(j, line1.endY, 2 * dy1);
      addJ(j, line2.startX, 2 * dx2);
      addJ(j, line2.endX, -2 * dx2);
      addJ(j, line2.startY, 2 * dy2);
      addJ(j, line2.endY, -2 * dy2);
      return j;
    },
  };
}

// ═══════════════════════════════════════════════════════════════════════
// 9. Symmetric — two points symmetric across an axis line.
//    mid = (a + b) / 2  must lie on axis  → collinear(mid, axisStart, axisEnd)
//    (b - a) must be perpendicular to (axisEnd - axisStart) → dot = 0
//    2 residuals.
// ═══════════════════════════════════════════════════════════════════════

export function symmetricResidual(
  a: PointRef,
  b: PointRef,
  axisStart: PointRef,
  axisEnd: PointRef,
): ConstraintResidual[] {
  // r1: midpoint on axis — cross product of (mid - axisStart) with
  // (axisEnd - axisStart) = 0.
  // mid.x = (a.x + b.x) / 2; mid.y = (a.y + b.y) / 2
  // r1 = (mid.x - axisStart.x)*(axisEnd.y - axisStart.y)
  //    - (mid.y - axisStart.y)*(axisEnd.x - axisStart.x)
  // r2: perpendicular — (b - a) · (axisEnd - axisStart) = 0
  return [
    {
      compute: (v) => {
        const mx = (v[a.x]! + v[b.x]!) / 2;
        const my = (v[a.y]! + v[b.y]!) / 2;
        const sx = v[axisStart.x]!;
        const sy = v[axisStart.y]!;
        const ex = v[axisEnd.x]!;
        const ey = v[axisEnd.y]!;
        return (mx - sx) * (ey - sy) - (my - sy) * (ex - sx);
      },
      jacobian: (v) => {
        const mx = (v[a.x]! + v[b.x]!) / 2;
        const my = (v[a.y]! + v[b.y]!) / 2;
        const sx = v[axisStart.x]!;
        const sy = v[axisStart.y]!;
        const ex = v[axisEnd.x]!;
        const ey = v[axisEnd.y]!;
        const dx = ex - sx;
        const dy = ey - sy;
        const j: Jrow = {};
        // r = (mx-sx)*dy - (my-sy)*dx
        // ∂/∂a.x = (1/2)*dy,  ∂/∂b.x = (1/2)*dy
        // ∂/∂a.y = -(1/2)*dx, ∂/∂b.y = -(1/2)*dx
        addJ(j, a.x, 0.5 * dy);
        addJ(j, b.x, 0.5 * dy);
        addJ(j, a.y, -0.5 * dx);
        addJ(j, b.y, -0.5 * dx);
        // ∂/∂axisStart.x = -dy - (my - sy)*(-1) = -dy + (my - sy)
        addJ(j, axisStart.x, -dy + (my - sy));
        // ∂/∂axisStart.y = (mx - sx)*(-1) + dx = -(mx-sx) + dx
        addJ(j, axisStart.y, -(mx - sx) + dx);
        // ∂/∂axisEnd.x = -(my - sy)
        addJ(j, axisEnd.x, -(my - sy));
        // ∂/∂axisEnd.y = (mx - sx)
        addJ(j, axisEnd.y, mx - sx);
        return j;
      },
    },
    {
      compute: (v) => {
        const bax = v[b.x]! - v[a.x]!;
        const bay = v[b.y]! - v[a.y]!;
        const dx = v[axisEnd.x]! - v[axisStart.x]!;
        const dy = v[axisEnd.y]! - v[axisStart.y]!;
        return bax * dx + bay * dy;
      },
      jacobian: (v) => {
        const bax = v[b.x]! - v[a.x]!;
        const bay = v[b.y]! - v[a.y]!;
        const dx = v[axisEnd.x]! - v[axisStart.x]!;
        const dy = v[axisEnd.y]! - v[axisStart.y]!;
        const j: Jrow = {};
        addJ(j, a.x, -dx);
        addJ(j, b.x, dx);
        addJ(j, a.y, -dy);
        addJ(j, b.y, dy);
        addJ(j, axisStart.x, -bax);
        addJ(j, axisEnd.x, bax);
        addJ(j, axisStart.y, -bay);
        addJ(j, axisEnd.y, bay);
        return j;
      },
    },
  ];
}

// ═══════════════════════════════════════════════════════════════════════
// 10. Concentric — two circles share center. 2 residuals.
// ═══════════════════════════════════════════════════════════════════════

export function concentricResidual(
  c1: CircleRef,
  c2: CircleRef,
): ConstraintResidual[] {
  return [
    {
      compute: (v) => v[c1.centerX]! - v[c2.centerX]!,
      jacobian: () => {
        const j: Jrow = {};
        addJ(j, c1.centerX, 1);
        addJ(j, c2.centerX, -1);
        return j;
      },
    },
    {
      compute: (v) => v[c1.centerY]! - v[c2.centerY]!,
      jacobian: () => {
        const j: Jrow = {};
        addJ(j, c1.centerY, 1);
        addJ(j, c2.centerY, -1);
        return j;
      },
    },
  ];
}

// ═══════════════════════════════════════════════════════════════════════
// 11. Fix — handled outside residuals (variable.fixed = true). No
// residual builder needed. Documented for completeness.
// ═══════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════
// 12. Smooth — G1 continuity between the endpoint of one curve and the
// startpoint of another. Modelled as:
//   (a) the touching endpoints coincide (→ 2 coincident residuals)
//   (b) the outgoing tangent of segment-1 is parallel to the incoming
//       tangent of segment-2 (→ parallel residual on the two tangent
//       vectors).
//
// For the line-line case this is represented by two LineRef's sharing
// the touching point, with the tangent vectors being the lines' own
// direction vectors. Caller that needs smoothness across a Line / Arc
// passes the line's direction and the arc's end-tangent as two LineRefs.
// ═══════════════════════════════════════════════════════════════════════

export function smoothResidual(
  tangent1Start: PointRef,
  tangent1End: PointRef,
  tangent2Start: PointRef,
  tangent2End: PointRef,
): ConstraintResidual[] {
  const line1: LineRef = {
    startX: tangent1Start.x,
    startY: tangent1Start.y,
    endX: tangent1End.x,
    endY: tangent1End.y,
  };
  const line2: LineRef = {
    startX: tangent2Start.x,
    startY: tangent2Start.y,
    endX: tangent2End.x,
    endY: tangent2End.y,
  };
  return [
    // Coincident at the seam: tangent1End == tangent2Start.
    ...coincidentResidual(tangent1End, tangent2Start),
    // Parallel tangents.
    parallelResidual(line1, line2),
  ];
}

// ═══════════════════════════════════════════════════════════════════════
// Dimensional constraint residuals — Aligned / Radial / Diameter.
// DYN-2 already has Horizontal / Vertical / Angular as direct-propagation
// Move actions; their residual forms below let them participate in the
// solver when over-constrained systems need balancing.
// ═══════════════════════════════════════════════════════════════════════

/**
 * Horizontal distance constraint — b.x - a.x = target.
 */
export function horizontalDistanceResidual(
  a: PointRef,
  b: PointRef,
  target: number,
): ConstraintResidual {
  return {
    compute: (v) => v[b.x]! - v[a.x]! - target,
    jacobian: () => {
      const j: Jrow = {};
      addJ(j, a.x, -1);
      addJ(j, b.x, 1);
      return j;
    },
  };
}

/**
 * Vertical distance constraint — b.y - a.y = target.
 */
export function verticalDistanceResidual(
  a: PointRef,
  b: PointRef,
  target: number,
): ConstraintResidual {
  return {
    compute: (v) => v[b.y]! - v[a.y]! - target,
    jacobian: () => {
      const j: Jrow = {};
      addJ(j, a.y, -1);
      addJ(j, b.y, 1);
      return j;
    },
  };
}

/**
 * Aligned distance constraint — straight-line distance between two
 * points equals target. Uses squared form for a well-behaved Jacobian:
 *   r = (b.x-a.x)² + (b.y-a.y)² - target²
 */
export function alignedDistanceResidual(
  a: PointRef,
  b: PointRef,
  target: number,
): ConstraintResidual {
  return {
    compute: (v) => {
      const dx = v[b.x]! - v[a.x]!;
      const dy = v[b.y]! - v[a.y]!;
      return dx * dx + dy * dy - target * target;
    },
    jacobian: (v) => {
      const dx = v[b.x]! - v[a.x]!;
      const dy = v[b.y]! - v[a.y]!;
      const j: Jrow = {};
      addJ(j, a.x, -2 * dx);
      addJ(j, b.x, 2 * dx);
      addJ(j, a.y, -2 * dy);
      addJ(j, b.y, 2 * dy);
      return j;
    },
  };
}

/**
 * Angular distance constraint — angle ∠(vertex→a, vertex→b) = target
 * (radians). Implemented via the vector-equation form:
 *   dot  = (a-v)·(b-v)
 *   cross= (a-v)×(b-v)
 *   r = atan2(cross, dot) - target
 *
 * The atan2 Jacobian is derived from the quotient cross/dot via
 *   ∂atan2/∂cross = dot/(cross²+dot²)
 *   ∂atan2/∂dot   = -cross/(cross²+dot²)
 *
 * Handles the degenerate (cross²+dot²=0) case by returning a zero
 * Jacobian (caller should have a well-defined vertex).
 */
export function angularConstraintResidual(
  vertex: PointRef,
  a: PointRef,
  b: PointRef,
  target: number,
): ConstraintResidual {
  return {
    compute: (v) => {
      const ax = v[a.x]! - v[vertex.x]!;
      const ay = v[a.y]! - v[vertex.y]!;
      const bx = v[b.x]! - v[vertex.x]!;
      const by = v[b.y]! - v[vertex.y]!;
      const dot = ax * bx + ay * by;
      const cross = ax * by - ay * bx;
      if (dot * dot + cross * cross < 1e-18) return -target;
      return Math.atan2(cross, dot) - target;
    },
    jacobian: (v) => {
      const ax = v[a.x]! - v[vertex.x]!;
      const ay = v[a.y]! - v[vertex.y]!;
      const bx = v[b.x]! - v[vertex.x]!;
      const by = v[b.y]! - v[vertex.y]!;
      const dot = ax * bx + ay * by;
      const cross = ax * by - ay * bx;
      const denom = cross * cross + dot * dot;
      const j: Jrow = {};
      if (denom < 1e-18) return j;
      const invDenom = 1 / denom;
      const kCross = dot * invDenom;
      const kDot = -cross * invDenom;

      // d(cross)/d(ax)=by, /d(ay)=-bx, /d(bx)=-ay, /d(by)=ax
      // d(dot)/d(ax)=bx,   /d(ay)=by,  /d(bx)=ax,  /d(by)=ay
      // a = (a.x - v.x), so da/d(a.x) = 1, da/d(v.x) = -1.
      const dAtan_dAx = kCross * by + kDot * bx;
      const dAtan_dAy = kCross * -bx + kDot * by;
      const dAtan_dBx = kCross * -ay + kDot * ax;
      const dAtan_dBy = kCross * ax + kDot * ay;

      addJ(j, a.x, dAtan_dAx);
      addJ(j, a.y, dAtan_dAy);
      addJ(j, b.x, dAtan_dBx);
      addJ(j, b.y, dAtan_dBy);
      addJ(j, vertex.x, -dAtan_dAx - dAtan_dBx);
      addJ(j, vertex.y, -dAtan_dAy - dAtan_dBy);
      return j;
    },
  };
}

/**
 * Radial constraint — circle radius = target (direct 1-D equation).
 */
export function radialResidual(
  circle: CircleRef,
  target: number,
): ConstraintResidual {
  return {
    compute: (v) => v[circle.radius]! - target,
    jacobian: () => {
      const j: Jrow = {};
      addJ(j, circle.radius, 1);
      return j;
    },
  };
}

/**
 * Diameter constraint — 2*radius = target.
 */
export function diameterResidual(
  circle: CircleRef,
  target: number,
): ConstraintResidual {
  return {
    compute: (v) => 2 * v[circle.radius]! - target,
    jacobian: () => {
      const j: Jrow = {};
      addJ(j, circle.radius, 2);
      return j;
    },
  };
}

/**
 * Arc radial — identical to radialResidual but on ArcRef.
 */
export function arcRadialResidual(
  arc: ArcRef,
  target: number,
): ConstraintResidual {
  return {
    compute: (v) => v[arc.radius]! - target,
    jacobian: () => {
      const j: Jrow = {};
      addJ(j, arc.radius, 1);
      return j;
    },
  };
}
